import { FC, useEffect, useState } from 'react';
import cn from 'classnames';
import Link from 'next/link';
import { AnimatedCard } from '@/components/animation/AnimatedCard';
import { VacancyCardProps } from './VacancyCard.props';
import { useLocaleHook, useMediaQueryWidthPicker } from '@/hooks';
import ArrowIcon from '@/public/icons/team/right-arrow.svg';
import CityIcon from '@/public/icons/team/city.svg';
import { useTranslation } from 'react-i18next';

export const VacancyCard: FC<VacancyCardProps> = ({
  title,
  className,
  linkUrl = '/',
  vacancies,
  cities,
  format,
  type,
}) => {
  const { t } = useTranslation('common');
  const btnText = t('learnMore');
  const locale = useLocaleHook();
  const isUk = locale === 'uk';
  const { device } = useMediaQueryWidthPicker();

  const [showAll, setShowAll] = useState(false);
  const [maxVisible, setMaxVisible] = useState(4);
  const visibleCities = showAll ? cities : cities.slice(0, maxVisible);
  const hasHiddenCities = cities.length > maxVisible;

  useEffect(() => {
    if (device === 'mobile') setMaxVisible(4);
    if (device === 'tablet') setMaxVisible(8);
    if (device === 'desktop') setMaxVisible(6);
  }, [device]);

  return (
    <AnimatedCard
      className={cn(
        'vacancy-card relative p-8 transition-all duration-300 dark:rounded dark:border dark:border-darkFullLight md:flex md:h-full md:flex-col md:p-[32px]',
        className,
      )}
    >
      <Link
        href={linkUrl}
        prefetch={false}
        aria-label={
          isUk ? 'Перейти на сторінку вакансії' : 'Go to vacancy page'
        }
        className="absolute left-0 top-0 h-full w-full"
      ></Link>
      <div className="vacancy-card__inner-wrapper items-end justify-between md:flex">
        <div className="vacancy-card__main-info mb-[28px] md:mb-0">
          <div className={`vacancy-card__title mb-3`}>{title}</div>
          <div className="vacancy-card__tags mb-[28px] flex flex-wrap">
            <div className="vacancy-card__tag w-fit px-3 py-1">{format}</div>
            <div className="vacancy-card__tag w-fit px-3 py-1">{type}</div>
            {visibleCities.map((city, idx) => (
              <div
                key={idx}
                className="vacancy-card__tag vacancy-card__tag--city w-fit px-3 py-1 md:px-[14px] md:py-[6px]"
              >
                <CityIcon /> {city}
              </div>
            ))}

            {hasHiddenCities && !showAll && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  setShowAll(true);
                }}
                className="vacancy-card__tag z-[9999] rounded border border-red-600 px-3 py-1 text-red-600"
              >
                {isUk ? 'Показати ще' : 'Show more'}
              </button>
            )}
          </div>
          <div
            className="vacancy-card__description line-clamp-3 md:line-clamp-2"
            dangerouslySetInnerHTML={{ __html: vacancies.teamExcerpt }}
          />
        </div>
        <div className="vacancy-card__button-wrapper flex w-fit">
          <button className="vacancy-card__button flex">
            <span className="vacancy-card__button-text md:hidden">
              {btnText}
            </span>
            <span className="vacancy-card__button-icon">
              <ArrowIcon />
            </span>
          </button>
        </div>
      </div>
    </AnimatedCard>
  );
};
